import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createDevice($data: createDeviceInput!) {
    createDevice(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createDeviceRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateDeviceInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'device/create_device',
  async (params: {data: TYPES.CreateDeviceInput;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createDevice;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createDeviceSlice = createSlice({
  name: 'create_device',
  initialState: initialState,
  reducers: {
    createDeviceClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createDeviceRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createDeviceRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createDeviceRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createDeviceClear } = createDeviceSlice.actions;

export const createDevice = createSelector(
  (state: RootState) => state.device.createDevice.result,
  (state: RootState) => state.device.createDevice.error,
  (state: RootState) => state.device.createDevice.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createDeviceSlice.reducer;